import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import PageSeo, {
  articleJsonLd,
  breadcrumbJsonLd,
  keywordSets,
} from '../PageSeo';

// ✅ pakai gambar yang kamu sudah siapin (jangan diubah)
import heroB5 from '../images/denza-b5-hero.jpg';
import dmoBadge from '../images/denza-b5-dmo.jpg';
import disusP from '../images/denza-b5-disus-p.jpg';
import diffLocks from '../images/denza-b5-diff-lock.jpg';
import battery from '../images/denza-b5-battery.jpg';
import adas from '../images/denza-b5-adas.jpg';
import voice from '../images/denza-b5-voice.jpg';

function FiturB5() {
  const waLink =
    'https://api.whatsapp.com/send?phone=6281286864825&text=Halo%20saya%20mau%20tau%20info%20Denza%20B5%20nya';

  return (
    <Box
      sx={{ padding: { xs: 2, sm: 4 }, maxWidth: '1200px', margin: '0 auto' }}
    >
      <PageSeo
        title='Fitur Danza B5 Indonesia | DM-O, 4x4, DiSus-P, BYD BSD'
        description='Kupas fitur Danza B5: DM-O plug-in hybrid, AWD 4x4, DiSus-P, diff lock, Blade Battery, ADAS, EV range 90 km WLTC, dan booking di BYD BSD Serpong.'
        keywords={[
          ...keywordSets.localDealer,
          'Danza B5 Indonesia',
          'fitur Danza B5',
          'spesifikasi Danza B5',
          'Danza B5 DM-O',
          'Danza B5 DiSus-P',
        ]}
        path='/blog/fitur-denza-b5'
        image={heroB5}
        type='article'
        jsonLd={[
          articleJsonLd({
            title: 'Fitur Danza B5 Indonesia | DM-O, 4x4, DiSus-P',
            description:
              'Rangkuman fitur Danza B5 Indonesia, termasuk DM-O, 4x4, DiSus-P, Blade Battery, ADAS, dan EV range WLTC.',
            image: heroB5,
            path: '/blog/fitur-denza-b5',
          }),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: 'Fitur Danza B5', path: '/blog/fitur-denza-b5' },
          ]),
        ]}
      >
        <title>
          Fitur & Spesifikasi DANZA B5 (DM-O) | Dealer BYD BSD Serpong
        </title>
        <meta
          name='description'
          content='Kupas fitur dan spesifikasi DANZA B5 (DM-O): output sistem 425 kW dan 760 Nm, 0–100 km/jam 4,8 detik, baterai 31,8 kWh BYD Blade LFP, EV range 90 km (WLTC), 4x4 dengan diff lock depan-belakang, opsi suspensi hidrolik DiSus-P (Leopard), serta paket ADAS.'
        />
        <meta
          name='keywords'
          content='DANZA B5, Danza B5 DM-O, spesifikasi Danza B5, fitur Danza B5, DiSus-P, differential lock, baterai 31.8 kWh, 90 km WLTC, BYD BSD Serpong'
        />
        <link
          rel='canonical'
          href='https://bydbsdserpong.com/blog/fitur-denza-b5'
        />
      </PageSeo>

      <br />
      <br />
      <br />
      <br />

      <Typography variant='h3' component='h1' fontWeight='bold' gutterBottom>
        Fitur & Spesifikasi DANZA B5 (DM-O) — SUV 4x4 Plug-in Hybrid Berkarakter
        Off-road
      </Typography>

      <Typography
        variant='body1'
        paragraph
        sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
      >
        DANZA B5 hadir sebagai SUV ladder-frame 4x4 dengan pendekatan yang
        berbeda: rasa off-road tetap dapet, tapi responsnya modern berkat sistem
        plug-in hybrid DM-O. Di halaman ini, kamu bisa lihat rangkuman fitur
        utama dan angka spesifikasi penting yang sering dicari sebelum test
        drive atau booking.
      </Typography>

      {/* HERO IMAGE */}
      <Box sx={{ my: 4 }}>
        <img
          src={heroB5}
          alt='DANZA B5'
          style={{ width: '100%', borderRadius: 12, display: 'block' }}
          loading='lazy'
        />
      </Box>

      {/* SECTION: DM-O POWERTRAIN */}
      <Box sx={{ my: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          DM-O Super Hybrid: Tenaga Besar, Respons Instan
        </Typography>

        <img
          src={dmoBadge}
          alt='DM-O Super Hybrid DANZA B5'
          style={{
            width: '100%',
            marginBottom: 16,
            borderRadius: 12,
            display: 'block',
          }}
          loading='lazy'
        />

        <Typography variant='body1' paragraph>
          Sistem <strong>1.5T DM-O Super Hybrid</strong> memadukan mesin 1.5L
          turbo dan motor listrik untuk penggerak AWD. Output sistemnya diklaim
          mencapai <strong>425 kW</strong> dan <strong>760 Nm</strong>, dengan
          akselerasi <strong>0–100 km/jam 4,8 detik</strong>. Dengan karakter
          torsi instan dari motor listrik, tarikan terasa agresif namun tetap
          halus untuk dipakai harian. (
          <a
            href='https://www.carexpert.com.au/car-reviews/2026-denza-b5-review-quick-drive'
            target='_blank'
            rel='noopener noreferrer'
          >
            Carexpert
          </a>
          )
        </Typography>
      </Box>

      {/* SECTION: DiSus-P */}
      <Box sx={{ my: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          DiSus-P (Varian Leopard): Ground Clearance & Kontrol Lebih Fleksibel
        </Typography>

        <img
          src={disusP}
          alt='DiSus-P DANZA B5'
          style={{
            width: '100%',
            marginBottom: 16,
            borderRadius: 12,
            display: 'block',
          }}
          loading='lazy'
        />

        <Typography variant='body1' paragraph>
          Untuk kamu yang mau kemampuan off-road lebih serius, varian{' '}
          <strong>B5 Leopard</strong> membawa{' '}
          <strong>DiSus-P adjustable hydraulic suspension</strong>. Sistem ini
          memungkinkan pengaturan ketinggian dan karakter suspensi sesuai
          kebutuhan—lebih stabil di jalan raya, atau lebih tinggi saat butuh
          melewati rintangan. Danza juga menyebut ground clearance Leopard bisa
          diatur dari <strong>220 mm</strong> hingga <strong>310 mm</strong>. (
          <a
            href='https://www.carexpert.com.au/car-reviews/2026-denza-b5-review-quick-drive'
            target='_blank'
            rel='noopener noreferrer'
          >
            Carexpert
          </a>
          )
        </Typography>
      </Box>

      {/* SECTION: DIFF LOCKS */}
      <Box sx={{ my: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          4x4 dengan Diff Lock Depan & Belakang: Traksi Siap untuk Medan Sulit
        </Typography>

        <img
          src={diffLocks}
          alt='Differential Lock DANZA B5'
          style={{
            width: '100%',
            marginBottom: 16,
            borderRadius: 12,
            display: 'block',
          }}
          loading='lazy'
        />

        <Typography variant='body1' paragraph>
          DANZA B5 dibekali{' '}
          <strong>electromechanical differential locks</strong> di{' '}
          <strong>depan</strong> dan <strong>belakang</strong>. Saat salah satu
          roda kehilangan traksi (permukaan licin, batu, pasir, atau jalur tidak
          rata), sistem bantu distribusi torsi agar mobil tetap maju lebih
          konsisten. (
          <a
            href='https://www.carexpert.com.au/car-reviews/2026-denza-b5-review-quick-drive'
            target='_blank'
            rel='noopener noreferrer'
          >
            Carexpert
          </a>
          )
        </Typography>
      </Box>

      {/* SECTION: BATTERY + RANGE + CHARGING */}
      <Box sx={{ my: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          Baterai 31,8 kWh Blade LFP + EV Range 90 km (WLTC) + DC Fast Charging
          100 kW
        </Typography>

        <img
          src={battery}
          alt='Baterai DANZA B5'
          style={{
            width: '100%',
            marginBottom: 16,
            borderRadius: 12,
            display: 'block',
          }}
          loading='lazy'
        />

        <Typography variant='body1' paragraph>
          Untuk mode listrik penuh, DANZA B5 membawa baterai{' '}
          <strong>31,8 kWh BYD Blade (LFP)</strong> dengan klaim{' '}
          <strong>electric driving range 90 km (WLTC)</strong>. Untuk pengisian
          cepat, tercatat <strong>maksimum DC charge rate 100 kW</strong>. (
          <a
            href='https://www.carexpert.com.au/car-reviews/2026-denza-b5-review-quick-drive'
            target='_blank'
            rel='noopener noreferrer'
          >
            Carexpert
          </a>
          )
        </Typography>
      </Box>

      {/* SECTION: ADAS */}
      <Box sx={{ my: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          ADAS & Safety Assist: Bantuan Berkendara Level 2
        </Typography>

        <img
          src={adas}
          alt='ADAS DANZA B5'
          style={{
            width: '100%',
            marginBottom: 16,
            borderRadius: 12,
            display: 'block',
          }}
          loading='lazy'
        />

        <Typography variant='body1' paragraph>
          Untuk keselamatan, Danza menyebut adanya paket{' '}
          <strong>Advanced Driving Assistance System</strong> (Level 2),
          termasuk dukungan <strong>kamera 360 derajat</strong>. (
          <a
            href='https://www.carexpert.com.au/car-reviews/2026-denza-b5-review-quick-drive'
            target='_blank'
            rel='noopener noreferrer'
          >
            Carexpert
          </a>
          )
        </Typography>
      </Box>

      {/* SECTION: SMART FEATURES */}
      <Box sx={{ my: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          Fitur Pintar & Kenyamanan Kabin
        </Typography>

        <img
          src={voice}
          alt='Fitur kabin DANZA B5'
          style={{
            width: '100%',
            marginBottom: 16,
            borderRadius: 12,
            display: 'block',
          }}
          loading='lazy'
        />

        <Typography variant='body1' paragraph>
          Di sisi interior, B5 membawa layar instrumen digital{' '}
          <strong>12,3 inci</strong> dan layar tengah <strong>15,6 inci</strong>{' '}
          (rotating). Untuk varian Leopard juga disebut ada peningkatan material
          kabin (termasuk Nappa leather) dan detail kenyamanan lainnya. (
          <a
            href='https://www.carexpert.com.au/car-reviews/2026-denza-b5-review-quick-drive'
            target='_blank'
            rel='noopener noreferrer'
          >
            Carexpert
          </a>
          )
        </Typography>
      </Box>

      {/* CTA */}
      <Paper
        sx={{
          p: 4,
          mt: 6,
          backgroundColor: '#e3f2fd',
          borderLeft: '6px solid #1976d2',
          borderRadius: 2,
        }}
      >
        <Typography variant='h5' fontWeight='bold' gutterBottom>
          Siap Tahu Harga & Ketersediaan DANZA B5?
        </Typography>
        <Typography variant='body1'>
          Untuk info unit, promo, dan jadwal test drive, klik tombol WhatsApp di
          bawah.
        </Typography>

        <Box sx={{ mt: 1 }}>
          <a href={waLink} target='_blank' rel='noopener noreferrer'>
            Chat WhatsApp — DANZA B5
          </a>
        </Box>
      </Paper>
    </Box>
  );
}

export default FiturB5;
